'use client';

import { Xmark } from '@byway/icons';
import clsx from 'clsx';
import { useTranslations } from 'next-intl';
import { forwardRef } from 'react';
import { Dialog, Modal, ModalOverlay } from 'react-aria-components';
import { Button } from '~/components/Button';
import { Stack } from '~/components/Layout/Stack';
import styles from './Drawer.module.css';
import type { DrawerProps } from './Drawer.types';

const Drawer = forwardRef<HTMLDivElement, DrawerProps>(
  (
    {
      ariaLabel,
      children,
      close,
      isOpen = false,
      showAsDialogInDesktop = false,
      title,
      variant = 'default',
      maxWidth = 'none',
      noHeader = false,
      dataTestId = 'dialog',
      overlayRef
    },
    ref
  ) => {
    const t = useTranslations('aria');
    const survey = variant === 'survey';

    // Create the close button component
    const closeButton = (
      <Button variant="icon" isTransparent={survey} ariaLabel={t('close')} onPress={close}>
        <Xmark decorative size="large" />
      </Button>
    );

    return (
      <ModalOverlay
        ref={overlayRef}
        className={clsx(styles.modalOverlay)}
        isDismissable
        isOpen={isOpen}
        onOpenChange={close}
      >
        <Modal
          ref={ref}
          className={clsx(styles.modal, {
            [styles.survey]: survey,
            [styles.modalDesktop]: showAsDialogInDesktop || survey,
            [styles.modalAsDialog]: showAsDialogInDesktop
          })}
          style={
            {
              '--drawer-max-width': typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth
            } as React.CSSProperties
          }
        >
          <Dialog
            className={clsx({
              [styles.dialog]: !showAsDialogInDesktop && !survey,
              [styles.dialogInDesktop]: showAsDialogInDesktop || survey
            })}
            aria-label={ariaLabel}
            aria-labelledby="drawer-title"
            data-test-id={dataTestId}
          >
            <Stack space="--by-space-200">
              <div className={styles.header}>
                {title && (
                  <span className={styles.title} id="drawer-title">
                    {title}
                  </span>
                )}
                <span
                  className={clsx(styles.closeButton, {
                    [styles.closeButtonAbsolute]: noHeader
                  })}
                >
                  {closeButton}
                </span>
              </div>
              <div className={styles.content}>{children}</div>
            </Stack>
          </Dialog>
        </Modal>
      </ModalOverlay>
    );
  }
);

Drawer.displayName = 'Drawer';

export { Drawer };
